import moment from 'moment';
import pluralize from 'pluralize';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Icon} from 'components';
import {OnlineSession} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {formatDate} from 'utils';

type Props = {
    session: OnlineSession;
    timeZone: string;
    dateFormat: string;
    timeFormat: string;
    className?: string;
}

export const SessionSchedule: React.FC<Props> = ({session, timeZone, dateFormat, timeFormat, className}) => {
    const {t} = useTranslation();

    const dateTimeFormatCall = useMemo(() => `${timeFormat}, ${dateFormat}`, [timeFormat, dateFormat]);

    const scheduleLabel = useMemo(() => {
        if (!session?.schedule) {
            return null;
        }

        const {startDate, endDate} = session.schedule;

        if (!endDate) {
            return formatDate(startDate, timeZone, dateTimeFormatCall);
        }

        const momentStart = moment(startDate);
        const momentEnd = moment(endDate);

        const isSameDay = momentStart.isSame(momentEnd, 'day');
        const format = isSameDay ? timeFormat : dateTimeFormatCall;
        const start = formatDate(startDate, timeZone, format);
        const end = formatDate(endDate, timeZone, format);

        let interval = `${start} - ${end},`;
        if (isSameDay) {
            interval += ` ${formatDate(startDate, timeZone, dateFormat)}`;
        }

        const durationMs = momentEnd.diff(momentStart);
        const duration = moment.duration(durationMs);
        const durationLabels = [];
        if (duration.get('hours') > 0) {
            durationLabels.push(pluralize(t(TRANSLATION_KEYS.AGENT_JOIN_SESSION_PAGE.HOUR_LABEL), duration.get('hours'), true));
        }
        if (duration.get('minutes') > 0) {
            durationLabels.push(pluralize(t(TRANSLATION_KEYS.AGENT_JOIN_SESSION_PAGE.MINUTE_LABEL), duration.get('minutes'), true));
        }

        return `${interval} (${durationLabels.join(' ')})`;
    }, [session?.schedule, timeFormat, dateTimeFormatCall, timeZone, dateFormat, t]);

    return (
        <div className={className}>
            <Icon name='calendar' color='default-dimmed' className='tw-mr-2' />
            <div>{scheduleLabel}</div>
        </div>
    );
};
